import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const LOOKBACK_DAYS = 30;

// Notification `type` values counted by functions/metrics/pushCounters.js.
// Keep this list in sync with PUSH_TYPES there — a type counted server-side but
// missing here is stored in Firestore yet never shown. Order runs the
// co-parent-driven types first, proactive reminders last, so the stack reads
// roughly from most to least frequent.
const PUSH_TYPES = [
  { key: 'messages', label: 'Messages', color: '#1B6B4A' },
  { key: 'expenses', label: 'Expenses', color: '#3BA676' },
  { key: 'swaps', label: 'Swaps', color: '#F5C842' },
  { key: 'activities', label: 'Activities', color: '#F2886D' },
  { key: 'reminders', label: 'Reminders', color: '#6C8FB5' },
  { key: 'handoffReminder', label: 'Handoff', color: '#9B7BB8' },
];

// The platform split shown as tiles, in the order iOS · Unknown · Android so the
// three tiles sum exactly to the by-type total. `unknown` holds sends to devices
// whose platform couldn't be resolved (older tokens) and shrinks as tokens
// re-register with a platform.
const PLATFORMS = [
  { key: 'ios', label: 'iOS', color: '#1E2328' },
  { key: 'unknown', label: 'Unknown', color: '#B8B2A8' },
  { key: 'android', label: 'Android', color: '#3BA676' },
];

function utcDateKey(date) {
  return date.toISOString().slice(0, 10);
}

// Pushes are bursty — plenty of days have no sends and therefore no doc. Build
// the full window so the x-axis stays evenly spaced and gaps read as zero
// rather than silently collapsing two distant days next to each other.
function buildWindow(docs) {
  const byDate = new Map((docs || []).map((d) => [d.id, d]));
  const rows = [];
  const cursor = new Date();
  cursor.setUTCDate(cursor.getUTCDate() - (LOOKBACK_DAYS - 1));
  for (let i = 0; i < LOOKBACK_DAYS; i += 1) {
    const key = utcDateKey(cursor);
    const byType = byDate.get(key)?.byType || {};
    const row = { date: key.slice(5) };
    for (const { key: type } of PUSH_TYPES) row[type] = byType[type] || 0;
    rows.push(row);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return rows;
}

// Sum a byPlatform map across the window docs for the platform tiles.
function sumPlatformWindow(docs, platform) {
  return (docs || []).reduce((sum, d) => sum + (d.byPlatform?.[platform] || 0), 0);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  const visible = payload.filter((p) => (p.value || 0) > 0);
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm space-y-1">
      <p className="font-semibold text-ink-800">{label}</p>
      <p className="text-xs text-ink-500 font-medium">Total: {total}</p>
      {visible.length === 0 ? (
        <p className="text-xs text-ink-400">No pushes</p>
      ) : (
        visible.map((p) => (
          <p key={p.dataKey} className="text-ink-500 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span>{p.name}:</span>
            <span>{p.value?.toLocaleString() ?? 0}</span>
          </p>
        ))
      )}
    </div>
  );
};

// Local tile rather than StatCard — StatCard stamps a "today" badge on every
// value, which would be wrong for lifetime counts.
const PlatformTile = ({ label, color, lifetime, window: windowCount }) => (
  <div className="card p-4 flex flex-col gap-1.5 min-w-0">
    <p className="text-xs font-semibold uppercase tracking-wide text-ink-400 flex items-center gap-2">
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      {label}
    </p>
    <p className="font-display text-2xl font-semibold text-ink-900 leading-none">
      {lifetime.toLocaleString()}
    </p>
    <p className="text-xs text-ink-400">{windowCount.toLocaleString()} in last 30d</p>
  </div>
);

const PushSentPanel = ({ pushStats, pushTotals }) => {
  // Defensive: the subscription already drops the `_totals` sentinel, but its
  // `_`-prefixed id sorts after date keys so a range query can surface it —
  // summing byPlatform over it would add lifetime onto the window. Guard here
  // too so this panel is correct regardless of how it's fed.
  const dailyDocs = useMemo(
    () => (pushStats || []).filter((d) => d.id !== '_totals'),
    [pushStats]
  );

  const data = useMemo(() => buildWindow(dailyDocs), [dailyDocs]);

  const windowByType = useMemo(() => {
    const acc = {};
    for (const { key } of PUSH_TYPES) {
      acc[key] = data.reduce((sum, row) => sum + (row[key] || 0), 0);
    }
    return acc;
  }, [data]);

  const windowTotal = useMemo(
    () => Object.values(windowByType).reduce((sum, n) => sum + n, 0),
    [windowByType]
  );

  const windowByPlatform = useMemo(
    () => Object.fromEntries(PLATFORMS.map((p) => [p.key, sumPlatformWindow(dailyDocs, p.key)])),
    [dailyDocs]
  );

  // Sends whose device platform couldn't be resolved (older tokens). Counted in
  // the total + chart, but shown as a footnote rather than a tile.
  const windowUnknown = useMemo(() => sumPlatformWindow(dailyDocs, 'unknown'), [dailyDocs]);
  const lifetimeUnknown = pushTotals?.byPlatform?.unknown || 0;

  const lifetimeTotal = pushTotals?.total || 0;
  const hasAnyData = lifetimeTotal > 0 || windowTotal > 0;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">Push notifications sent</h3>
        <span className="text-xs text-ink-400">
          Last 30 days · {windowTotal.toLocaleString()} sent
        </span>
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Counted when each push is handed to Expo, after notification-preference and
        quiet-hours filtering. Split by platform below; stacked by type in the chart.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {PLATFORMS.map(({ key, label, color }) => (
          <PlatformTile
            key={key}
            label={label}
            color={color}
            lifetime={pushTotals?.byPlatform?.[key] || 0}
            window={windowByPlatform[key] || 0}
          />
        ))}
      </div>

      {hasAnyData ? (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d6" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: '#57534e' }}
              axisLine={{ stroke: '#e5e0d6' }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis tick={{ fontSize: 11, fill: '#57534e' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(27, 107, 74, 0.05)' }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {PUSH_TYPES.map(({ key, label, color }, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={label}
                stackId="a"
                fill={color}
                radius={i === PUSH_TYPES.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="border-2 border-dashed border-surface-200 rounded-xl p-8 text-center">
          <p className="text-sm text-ink-500 mb-1">No pushes counted yet.</p>
          <p className="text-xs text-ink-400">
            Counters start at the next notification sent. There is no historical send log
            to backfill from, so only sends after this shipped are counted.
          </p>
        </div>
      )}

      <p className="text-[11px] text-ink-400 mt-4 pt-3 border-t border-ink-100">
        Platform tiles show lifetime totals and sum to the chart; the chart covers the last 30 days.
        {(windowUnknown > 0 || lifetimeUnknown > 0) && (
          <>
            {' '}
            “Unknown” is sends to devices whose platform isn’t recorded yet — older push tokens that
            re-register with a platform as those apps update, so it shrinks over time.
          </>
        )}
      </p>
    </div>
  );
};

export default PushSentPanel;
